/**
 * Engineer “request certificate” modal: product-controlled allowlist until more PDF types ship.
 *
 * Resolution order:
 * 1. REACT_APP_CERTIFICATE_REQUEST_TEMPLATE_IDS — comma-separated DB ids (strongest).
 * 2. schemaJson.requestFlowTemplateKey matches REQUEST_FLOW_SCHEMA_KEYS.
 * 3. Fallback: approved PDF checklist + “breakdown” in labels/path + exactly 107 AcroForm rows
 *    (CP17 Breakdown Master); if several match, newest updatedAt wins (single row).
 */

export const REQUEST_FLOW_SCHEMA_KEYS = ['cp17-breakdown-master'] as const;

export type RequestFlowTemplateKey = (typeof REQUEST_FLOW_SCHEMA_KEYS)[number];

/** When saving from the template wizard, stamp this key so the request modal can resolve without env vars. */
export function inferRequestFlowTemplateKeyFromWizardContext(parts: {
  originalPdfFileName?: string;
  certificatePrefix?: string;
  certificateTypeName?: string;
  templateLabel?: string;
}): RequestFlowTemplateKey | undefined {
  const name = String(parts.originalPdfFileName || '').toLowerCase();
  const meta = `${parts.certificatePrefix || ''} ${parts.certificateTypeName || ''} ${parts.templateLabel || ''}`.toLowerCase();
  const hay = `${name} ${meta}`.replace(/\\/g, '/');
  const cp17 = hay.includes('cp17');
  const breakdown = hay.includes('breakdown');
  if (cp17 && breakdown) return 'cp17-breakdown-master';
  return undefined;
}

export function parseCertificateRequestTemplateIdsFromEnv(): string[] {
  const raw = process.env.REACT_APP_CERTIFICATE_REQUEST_TEMPLATE_IDS || '';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function countWizardFields(schema: any): number {
  const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
  let n = 0;
  for (const s of screens) {
    const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
    for (const sec of sections) {
      const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      n += fields.length;
    }
  }
  return n;
}

export function isPdfChecklistTemplate(t: any): boolean {
  const hasRows = Array.isArray(t?.fields) && t.fields.length > 0;
  const screenCount = countWizardFields(t?.schemaJson);
  return hasRows || screenCount > 0;
}

function effectivePdfFieldCount(t: any): number {
  const fromSchema = countWizardFields(t?.schemaJson);
  if (fromSchema > 0) return fromSchema;
  return Array.isArray(t?.fields) ? t.fields.length : 0;
}

function schemaRequestFlowKey(schemaJson: unknown): string | undefined {
  if (!schemaJson || typeof schemaJson !== 'object') return undefined;
  const k = (schemaJson as { requestFlowTemplateKey?: unknown }).requestFlowTemplateKey;
  return typeof k === 'string' && k.trim() ? k.trim() : undefined;
}

function mentionsBreakdown(t: any): boolean {
  const blob = `${t.templateLabel || ''} ${t.certificateTypeName || ''} ${t.certificatePrefix || ''} ${t.pdfStoragePath || ''}`
    .toLowerCase()
    .replace(/\\/g, '/');
  return blob.includes('breakdown');
}

/** CP17(Breakdown)Master.pdf — 107 widgets per field audit */
const CP17_BREAKDOWN_FIELD_COUNT = 107;

export function filterTemplatesForCertificateRequest(arr: any[]): any[] {
  const base = arr.filter((t) => String(t?.reviewStatus || '') === 'APPROVED' && isPdfChecklistTemplate(t));

  const envIds = parseCertificateRequestTemplateIdsFromEnv();
  if (envIds.length > 0) {
    return base.filter((t) => envIds.includes(String(t.id)));
  }

  const keyed = base.filter((t) => {
    const k = schemaRequestFlowKey(t.schemaJson);
    return k ? (REQUEST_FLOW_SCHEMA_KEYS as readonly string[]).includes(k) : false;
  });
  if (keyed.length > 0) {
    keyed.sort((a, b) => {
      const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
      const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
      return tb - ta;
    });
    return keyed.slice(0, 1);
  }

  const fallback = base.filter((t) => mentionsBreakdown(t) && effectivePdfFieldCount(t) === CP17_BREAKDOWN_FIELD_COUNT);
  fallback.sort((a, b) => {
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    return tb - ta;
  });
  return fallback.slice(0, 1);
}
